/**
 * Resume Limit Utility
 * 
 * This utility provides functions to check and enforce the maximum resume limit per user.
 * Maximum allowed resumes: 300
 */

export const MAX_RESUME_LIMIT = 300;

/**
 * Get the current count of resumes from localStorage
 * @returns {number} The number of resumes stored
 */
export const getResumeCount = () => {
    try {
        const resumes = localStorage.getItem('resumes');
        if (!resumes) return 0;
        
        const resumesObj = JSON.parse(resumes);
        return Object.keys(resumesObj).length;
    } catch (error) {
        console.error('Error getting resume count:', error);
        return 0;
    }
};

/**
 * Check if the user has reached the maximum resume limit
 * @returns {boolean} True if limit is reached, false otherwise
 */
export const hasReachedResumeLimit = () => {
    const count = getResumeCount();
    return count >= MAX_RESUME_LIMIT;
};

/**
 * Show alert message when resume limit is exceeded
 * @param {string} action - The action being attempted ('create' or 'duplicate')
 */
export const showResumeLimitAlert = (action = 'create') => {
    const count = getResumeCount();
    const actionText = action === 'duplicate' ? 'duplicate' : 'create';
    
    alert(
        `Resume Limit Reached\n\n` +
        `You have reached the maximum limit of ${MAX_RESUME_LIMIT} resumes (Current: ${count}).\n\n` +
        `Please delete some resumes before you can ${actionText} new ones.`
    );
};

/**
 * Check if action can proceed, show alert if limit reached
 * @param {string} action - The action being attempted ('create' or 'duplicate')
 * @returns {boolean} True if action can proceed, false if limit reached
 */
export const canProceedWithResumeAction = (action = 'create') => {
    if (hasReachedResumeLimit()) {
        showResumeLimitAlert(action);
        return false;
    }
    return true;
};

/**
 * Get remaining resume slots
 * @returns {number} Number of resumes that can still be created
 */
export const getRemainingResumeSlots = () => {
    const count = getResumeCount();
    return Math.max(0, MAX_RESUME_LIMIT - count);
};
